// Do not put any dispatcher here.
// Only pure API calls
// The Facades should mimic exactly the corresponding API.

import { useCallback } from 'react';
import { useSweepApi } from '../../sweep';
import buildURLQuery from '../../../lib/buildURLQuery';
import { useDispatch } from 'react-redux';
import {
  populateFieldsInfoFromFunnelMaps,
  populateFieldsInfoFromFunnelMap,
} from '../../../sweep-fields/sweepFieldsInfoReducer';
import { FunnelMapWithFields } from '../../../sweep-fields/types';
import { FreePosition } from '../../../types/FreePosition';

export interface FunnelMapPutPayload {
  name: string;
  defaultCrmOrgId?: string | null;
  version: number; // Required for optimistic locking
  funnels: {
    funnelId: string;
    row: number;
    column: number;
    sectionId: string | null;
  }[];

  recordTypes?: {
    recordTypeName: string;
    objectName: string;
    row: number;
    column: number;
    leadingFieldName?: string | null;
    sectionId: string | null;
  }[];
  hubspotFunnels?: {
    id: string;
    row: number;
    column: number;
    objectName: string;
    leadingFieldName: string;
    sectionId: string | null;
  }[];
  thirdPartyFunnels?: {
    thirdPartyFunnelId: string;
    row: number;
    column: number;
    sectionId: string | null;
  }[];
  metadataElements?: {
    metadataId: string;
    row: number;
    column: number;
    color?: string;
    icon?: string | null;
    funnelLinks: FunnelLink[];
    crmOrgId: string;
    sectionId: string | null;
  }[];
  sections?: {
    sectionId: string;
    xyPosition: FreePosition;
    width: number;
    height: number;
    backgroundColor: string;
    name: string;
  }[];
  stickyNotes?: {
    stickyNoteId: string;
    xyPosition: FreePosition;
    width: number;
    height: number;
    backgroundColor: string;
    richText: string;
    sectionId: string | null;
    textScale: number; // Float
  }[];
  texts?: {
    textId: string;
    xyPosition: FreePosition;
    richText: string;
    width: number;
    height: number;
    sectionId: string | null;
    textScale: number; // Float
  }[];
}
interface FunnelMapPostPayload {
  name: string;
  defaultCrmOrgId?: string | null;
  funnels: any;
}

interface GetFunnelMapsPayload {
  includeFunnelsData?: boolean;
  includeFunnelId?: string;
}

export type FunnelMapFunnelsOnly = Pick<FunnelMap, 'funnels' | 'funnelsData'>;

const funnelMapsRoute = 'funnel-maps';

export const useFunnelMapApiFacade = () => {
  const sweepApi = useSweepApi();
  const dispatch = useDispatch();

  return {
    get_funnelMap: useCallback(
      async (id: string, includeFunnelsData?: boolean): Promise<FunnelMap | PartialFunnelMap> => {
        const response = await sweepApi.get(
          `/${funnelMapsRoute}/${id}?${buildURLQuery({ includeFunnelsData })}`,
        );

        const funnelMap = response.data as FunnelMapWithFields;
        dispatch(populateFieldsInfoFromFunnelMap({ funnelMap }));
        if (includeFunnelsData) {
          return funnelMap as FunnelMap;
        } else {
          return funnelMap as PartialFunnelMap;
        }
      },
      [dispatch, sweepApi],
    ),
    get_funnelMaps: useCallback(
      async ({
        includeFunnelsData,
        includeFunnelId,
      }: GetFunnelMapsPayload): Promise<FunnelMap[] | PartialFunnelMap[]> => {
        const getMapsResponse = await sweepApi.get(
          `/${funnelMapsRoute}?${buildURLQuery({ includeFunnelsData, includeFunnelId })}`,
        );

        if (includeFunnelsData) {
          const funnelMaps = getMapsResponse.data as FunnelMapWithFields[];
          dispatch(populateFieldsInfoFromFunnelMaps({ funnelMaps }));

          return funnelMaps as FunnelMap[];
        } else {
          return getMapsResponse.data as PartialFunnelMap[];
        }
      },
      [dispatch, sweepApi],
    ),
    post_funnelMap: useCallback(
      async (payload: FunnelMapPostPayload) => {
        const postResponse = await sweepApi.post(`/${funnelMapsRoute}`, payload);
        return postResponse.data as PartialFunnelMap;
      },
      [sweepApi],
    ),
    put_funnelMap: useCallback(
      async (id: string, payload: FunnelMapPutPayload) => {
        const response = await sweepApi.put(`/${funnelMapsRoute}/${id}`, payload);

        return response.data as FunnelMap | PartialFunnelMap;
      },
      [sweepApi],
    ),
    delete_funnelMap: useCallback(
      async (funnelMapId: string) => {
        await sweepApi.delete(`/${funnelMapsRoute}/${funnelMapId}`);
      },
      [sweepApi],
    ),
    get_funnelMapSweepFields: useCallback(
      async (funnelMapId: string) => {
        const response = await sweepApi.get(`/${funnelMapsRoute}/${funnelMapId}/fields`);
        return response.data as FunnelMapFieldsUsageData;
      },
      [sweepApi],
    ),
    updateIsPinnedApi: useCallback(
      async (id: string, payload: { isPinned: boolean }) => {
        const response = await sweepApi.patch(`/${funnelMapsRoute}/${id}/isPinned`, payload);

        return response.data as PartialFunnelMap;
      },
      [sweepApi],
    ),
    post_aiProcessMining: useCallback(
      async (payload: { objectName: string }) => {
        const { data } = await sweepApi.post<FunnelMapFunnelsOnly>(
          `/${funnelMapsRoute}/ai-process-mining`,
          payload,
        );
        return data;
      },
      [sweepApi],
    ),
  };
};
